import { useCallback } from "react";

type FormData = {
  name: string;
  customerId: number | null;
  workflowId: number | null;
};

type UseListDialogHandlersProps = {
  formData: FormData;
  setFormData: React.Dispatch<React.SetStateAction<FormData>>;
  selectedStatuses: number[];
  resetStatuses: () => void;
  showChangeWarning: (changeType: "customer" | "workflow", newValue: number | null) => void;
};

export const useListDialogHandlers = ({
  formData,
  setFormData,
  selectedStatuses,
  resetStatuses,
  showChangeWarning,
}: UseListDialogHandlersProps) => {
  const handleCustomerChange = useCallback(
    (value: string | string[] | undefined) => {
      const singleValue = Array.isArray(value) ? value[0] : value;
      const customerId = singleValue ? parseInt(singleValue) : null;

      if (selectedStatuses?.length > 0) {
        showChangeWarning("customer", customerId);
        return;
      }

      setFormData({
        ...formData,
        customerId,
        workflowId: null,
      });
      resetStatuses();
    },
    [formData, selectedStatuses?.length, setFormData, resetStatuses, showChangeWarning]
  );

  const handleWorkflowChange = useCallback(
    (value: string | string[] | undefined) => {
      const singleValue = Array.isArray(value) ? value[0] : value;
      const workflowId = singleValue ? parseInt(singleValue) : null;

      if (selectedStatuses?.length > 0) {
        showChangeWarning("workflow", workflowId);
        return;
      }

      setFormData({ ...formData, workflowId });
      resetStatuses();
    },
    [formData, selectedStatuses?.length, setFormData, resetStatuses, showChangeWarning]
  );

  const handleNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setFormData({ ...formData, name: e.target.value });
    },
    [formData, setFormData]
  );

  return {
    handleCustomerChange,
    handleWorkflowChange,
    handleNameChange,
  };
};
